import React, { useState, useEffect } from 'react';
import Layout from '../components/global/layout';
import ApiClient from '../methods/api/apiClient';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import loader from '../methods/loader';
import { useSearchParams } from 'react-router-dom';

const Zoom = () => {
  const user = useSelector((state) => state.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [zoomStatus, setZoomStatus] = useState({ connected: false, zoomEmail: null });
  const [zoomLoading, setZoomLoading] = useState(false);

  const getZoomStatus = () => {
    const groupId = user?.groupId?._id;
    if (!groupId) return;
    ApiClient.get('api/zoom/status', { groupId }).then(res => {
      if (res.success && res.data) {
        setZoomStatus(res.data);
      }
    });
  };

  useEffect(() => {
    const zoomParam = searchParams.get('zoom');
    if (zoomParam === 'success') {
      toast.success('Zoom connected successfully');
      searchParams.delete('zoom');
      setSearchParams(searchParams, { replace: true });
      getZoomStatus();
    } else if (zoomParam === 'error') {
      toast.error('Failed to connect Zoom. Please try again.');
      searchParams.delete('zoom');
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  useEffect(() => {
    getZoomStatus();
  }, [user?.groupId]);

  const connectZoom = () => {
    const groupId = user?.groupId?._id;
    if (!groupId) return;
    setZoomLoading(true);
    ApiClient.get('api/zoom/auth-url', { groupId }).then(res => {
      setZoomLoading(false);
      if (res.success && res.url) {
        window.location.href = res.url;
      } else {
        toast.error('Failed to get Zoom authorization URL');
      }
    });
  };

  const disconnectZoom = () => {
    const groupId = user?.groupId?._id;
    if (!groupId) return;
    Swal.fire({
      title: 'Disconnect Zoom?',
      text: 'This will remove the Zoom connection for your group.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, disconnect'
    }).then((result) => {
      if (result.isConfirmed) {
        loader(true);
        ApiClient.put('api/zoom/disconnect', { groupId }).then(res => {
          loader(false);
          if (res.success) {
            setZoomStatus({ connected: false, zoomEmail: null });
            toast.success('Zoom disconnected');
          }
        });
      }
    });
  };

  return (
    <Layout>
      <div className='flex items-center justify-between mb-6'>
        <h3 className='text-2xl font-semibold text-[#111827]'>Zoom Integration</h3>
      </div>

      <div className='shadow-box bg-white rounded-lg p-6 w-full'>
        <p className='text-sm text-gray-500 mb-6'>
          Connect your Zoom account to automatically pull meeting participant data for check-ins.
        </p>
        <div className='p-4 bg-gray-50 rounded-lg'>
          {zoomStatus.connected ? (
            <div className='flex items-center justify-between flex-wrap gap-4'>
              <div className='flex items-center gap-3'>
                <div className='w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center'>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 10.5V7C17 6.45 16.55 6 16 6H4C3.45 6 3 6.45 3 7V17C3 17.55 3.45 18 4 18H16C16.55 18 17 17.55 17 17V13.5L21 17.5V6.5L17 10.5Z" fill="#2D8CFF"/>
                  </svg>
                </div>
                <div>
                  <p className='text-sm font-medium text-gray-800'>Connected to Zoom</p>
                  {zoomStatus.zoomEmail && (
                    <p className='text-xs text-gray-500'>{zoomStatus.zoomEmail}</p>
                  )}
                </div>
              </div>
              <button
                className='bg-red-500 hover:bg-red-600 px-4 py-2 rounded-lg text-white text-sm font-medium transition-all'
                onClick={disconnectZoom}
              >
                Disconnect
              </button>
            </div>
          ) : (
            <div className='flex items-center justify-between flex-wrap gap-4'>
              <p className='text-sm text-gray-600'>No Zoom account connected</p>
              <button
                className='bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-white text-sm font-medium transition-all flex items-center gap-2'
                onClick={connectZoom}
                disabled={zoomLoading}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 10.5V7C17 6.45 16.55 6 16 6H4C3.45 6 3 6.45 3 7V17C3 17.55 3.45 18 4 18H16C16.55 18 17 17.55 17 17V13.5L21 17.5V6.5L17 10.5Z" fill="white"/>
                </svg>
                {zoomLoading ? 'Connecting...' : 'Connect Zoom'}
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Zoom;
